<!-- eslint-disable vue/valid-v-slot -->
<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="pageTitle"
              :subtitle="pageSubtitle"
            >
              <template v-slot:actions>
                <div class="d-flex justify-end align-center">
                    <v-btn
                      color="grey darken-1"
                      elevation="1"
                      x-small
                      @click="$router.go(-1)"
                      class="mr-2 px-3"
                      outlined
                    >
                      <v-icon left x-small>mdi-arrow-left</v-icon>
                      Back
                    </v-btn>
                    <v-btn
                      color="success"
                      elevation="1"
                      x-small
                      @click="exportToExcel"
                      :loading="isExporting"
                      class="mr-2 px-3"
                      outlined
                    >
                      <v-icon left x-small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                    <v-btn
                      color="primary"
                      elevation="1"
                      x-small
                      @click="$router.push('/seller-product-variants/create')"
                      class="px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Variant
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20">mdi-filter-variant</v-icon>
                    <span class="text-body-2 font-weight-medium">Filter Seller Product Variants</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="pt-2 px-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="searchKeyword"
                    label="Search Variants"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="Seller, product variant..."
                    hide-details
                    @click:clear="(searchKeyword = null), applyFilters()"
                    @keydown.enter="applyFilters()"
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filters.is_active"
                    :items="statusItems"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="applyFilters()"
                  ></v-autocomplete>
                </v-col>

                <!-- Brand Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filters.brand_name"
                    :items="brands"
                    item-text="description"
                    item-value="id"
                    label="Brand"
                    prepend-inner-icon="mdi-tag"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="applyFilters()"
                  ></v-autocomplete>
                </v-col>

                <!-- Category Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filters.category_id"
                    :items="categories"
                    item-text="name"
                    item-value="id"
                    label="Category"
                    prepend-inner-icon="mdi-shape"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="applyFilters()"
                  ></v-autocomplete>
                </v-col>

                <!-- Stock Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filters.stock_filter"
                    :items="stockFilterItems"
                    label="Stock Level"
                    prepend-inner-icon="mdi-package"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="applyFilters()"
                  ></v-autocomplete>
                </v-col>

                <!-- Show Deleted -->
                <v-col cols="12" md="1">
                  <v-switch
                    v-model="showDeleted"
                    label="Deleted"
                    @change="applyFilters()"
                    dense
                    hide-details
                    color="primary"
                  ></v-switch>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20">mdi-table</v-icon>
                    <span class="text-body-1 font-weight-medium">Variants List</span>
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-package-variant</v-icon>
                      {{ totalItems }} {{ totalItems === 1 ? "Variant" : "Variants" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="totalItems > 0">
                      {{ (currentPage - 1) * itemsPerPage + 1 }}-{{
                        Math.min(currentPage * itemsPerPage, totalItems)
                      }}
                      of {{ totalItems }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Bulk delete button shown when items are selected -->
                    <v-btn
                      v-if="selectedItems.length > 0"
                      color="error"
                      x-small
                      class="mr-2"
                      @click="confirmBulkDelete"
                    >
                      <v-icon left x-small>mdi-delete-sweep</v-icon>
                      Delete ({{ selectedItems.length }})
                    </v-btn>
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading variants..." />
                </div>
              </section>
              <!-- Data Table -->
              <v-data-table
                v-else
                v-model="selectedItems"
                :headers="headers"
                :items="sellerProductVariants"
                :server-items-length="totalItems"
                :page.sync="currentPage"
                :items-per-page="itemsPerPage"
                hide-default-footer
                show-select
                item-key="id"
                class="enhanced-table"
              >
                <!-- Row Number Column -->
                <template #item.row_number="{ index }">
                  <div class="text-center text-caption grey--text">
                    {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                  </div>
                </template>

                <!-- Seller Code Column -->
                <template #item.seller_code="{ item }">
                  <div class="text-body-2">
                    {{ item.seller && item.seller.guest_code ? item.seller.guest_code : "N/A" }}
                  </div>
                </template>

                <!-- Seller Column -->
                <template #item.seller="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2" color="primary">
                      <span class="white--text text-caption">
                        {{ getInitials(item.seller) }}
                      </span>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium text-body-2">
                        {{ getSellerName(item.seller) }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ item.seller ? item.seller.email : "N/A" }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- SKU Column -->
                <template #item.sku="{ item }">
                  <div class="text-body-2">
                    {{ item.product_variant ? item.product_variant.sku_id : "N/A" }}
                  </div>
                </template>

                <!-- Product Variant Column -->
                <template #item.product_variant="{ item }">
                  <div class="font-weight-medium text-body-2">
                    {{ item.product_variant ? item.product_variant.name : "N/A" }}
                  </div>
                </template>

                <!-- Brand Column -->
                <template #item.brand="{ item }">
                  <div class="text-body-2" v-if="item.seller_product && item.seller_product.product">
                    {{ item.seller_product.product.brand_name || "N/A" }}
                  </div>
                  <span v-else class="text-caption grey--text">N/A</span>
                </template>

                <!-- Category Column -->
                <template #item.category="{ item }">
                  <div class="text-body-2">
                    {{ item.seller_product && item.seller_product.product && item.seller_product.product.category ? item.seller_product.product.category.name : "N/A" }}
                  </div>
                </template>

                <!-- HSN Code Column -->
                <template #item.hsn_code="{ item }">
                  <div class="text-body-2">
                    {{ item.seller_product && item.seller_product.product && item.seller_product.product.hsn_code ? item.seller_product.product.hsn_code : "N/A" }}
                  </div>
                </template>

                <!-- MRP Column -->
                <template #item.mrp="{ item }">
                  <div class="text-body-2 font-weight-medium">
                    ₹{{ Number(item.maximum_retail_price || 0).toFixed(2) }}
                  </div>
                </template>

                <!-- Stock Column -->
                <template #item.stock="{ item }">
                  <div class="text-body-2">
                    {{ item.stock }}
                  </div>
                </template>

                <!-- Seller Discount Column -->
                <template #item.seller_discount="{ item }">
                  <div class="text-body-2">
                    <template v-if="item.seller_discount">
                      ₹{{ ((Number(item.maximum_retail_price || 0) * Number(item.seller_discount)) / 100).toFixed(2) }}
                      <div class="text-caption grey--text">
                        ({{ item.seller_discount }}%)
                      </div>
                    </template>
                    <template v-else>N/A</template>
                  </div>
                </template>

                <!-- HT Discount Column -->
                <template #item.ht_discount="{ item }">
                  <div class="text-body-2">
                    <template v-if="item.ht_discount">
                      ₹{{ ((Number(item.maximum_retail_price || 0) * Number(item.ht_discount)) / 100).toFixed(2) }}
                      <div class="text-caption grey--text">
                        ({{ item.ht_discount }}%)
                      </div>
                    </template>
                    <template v-else>N/A</template>
                  </div>
                </template>

                <!-- Consumer Discount Column -->
                <template #item.consumer_discount="{ item }">
                  <div class="text-body-2">
                    <template v-if="item.consumer_discount">
                      ₹{{ ((Number(item.maximum_retail_price || 0) * Number(item.consumer_discount)) / 100).toFixed(2) }}
                      <div class="text-caption grey--text">
                        ({{ item.consumer_discount }}%)
                      </div>
                    </template>
                    <template v-else>N/A</template>
                  </div>
                </template>

                <!-- Doctor Discount Column -->
                <template #item.doctor_discount="{ item }">
                  <div class="text-body-2">
                    <template v-if="item.doctor_discount">
                      ₹{{ ((Number(item.maximum_retail_price || 0) * Number(item.doctor_discount)) / 100).toFixed(2) }}
                      <div class="text-caption grey--text">
                        ({{ item.doctor_discount }}%)
                      </div>
                    </template>
                    <template v-else>N/A</template>
                  </div>
                </template>

                <!-- Created By Column -->
                <template #item.created_by="{ item }">
                  <div v-if="item.created_by" class="text-body-2">
                    {{ item.created_by.first_name }} {{ item.created_by.last_name }}
                  </div>
                  <div v-else class="text-caption grey--text">System</div>
                </template>

                <!-- Created Date Column -->
                <template #item.created_at="{ item }">
                  <div class="text-body-2">
                    {{ formatDate(item.created_at) }}
                  </div>
                </template>

                <!-- Actions Column -->
                <template #item.actions="{ item }">
                  <div class="d-flex justify-center">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="primary"
                          @click="editItem(item)"
                          class="mr-1"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Edit</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="info"
                          @click="viewItem(item)"
                          class="mr-1"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-eye</v-icon>
                        </v-btn>
                      </template>
                      <span>View</span>
                    </v-tooltip>

                    <v-tooltip bottom v-if="!item.is_deleted">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="error"
                          @click="confirmDelete(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Delete</span>
                    </v-tooltip>

                    <v-tooltip bottom v-else>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="success"
                          @click="restoreItem(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-restore</v-icon>
                        </v-btn>
                      </template>
                      <span>Restore</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-data-table>

              <!-- Pagination Section -->
              <v-divider></v-divider>
              <v-card-actions class="justify-center py-2">
                <v-pagination
                  v-model="currentPage"
                  :length="Math.ceil(totalItems / itemsPerPage)"
                  :total-visible="7"
                  color="primary"
                  @input="fetchData"
                  circle
                ></v-pagination>
              </v-card-actions>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog.show" max-width="400" persistent>
        <v-card class="rounded-xl">
          <v-card-title class="pa-2">
            <v-icon color="error" class="mr-2">mdi-delete-alert</v-icon>
            <span class="text-h6">Confirm Delete</span>
          </v-card-title>

          <v-card-text class="pa-2">
            <p class="mb-2">
              Are you sure you want to delete this seller product variant?
            </p>
            <div class="grey lighten-4 pa-1 rounded">
              <div class="text-caption">
                <strong>Seller:</strong>
                {{ getSellerName(deleteDialog.item?.seller) }}<br />
                <strong>Variant:</strong>
                {{ deleteDialog.item?.product_variant?.name || "N/A" }}
              </div>
            </div>
          </v-card-text>

          <v-card-actions class="pa-2">
            <v-spacer></v-spacer>
            <v-btn text @click="deleteDialog.show = false">Cancel</v-btn>
            <v-btn
              color="error"
              @click="deleteItem"
              :loading="deleteDialog.loading"
            >
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Bulk Delete Confirmation Dialog -->
      <v-dialog v-model="bulkDeleteDialog.show" max-width="420" persistent>
        <v-card class="rounded-xl">
          <v-card-title class="pa-2">
            <v-icon color="error" class="mr-2">mdi-delete-sweep</v-icon>
            <span class="text-h6">Confirm Bulk Delete</span>
          </v-card-title>

          <v-card-text class="pa-2">
            <p class="mb-2">
              Are you sure you want to delete
              <strong>{{ selectedItems.length }} selected variant(s)</strong>?
              This action cannot be undone.
            </p>
          </v-card-text>

          <v-card-actions class="pa-2">
            <v-spacer></v-spacer>
            <v-btn text @click="bulkDeleteDialog.show = false">Cancel</v-btn>
            <v-btn
              color="error"
              @click="bulkDeleteItems"
              :loading="bulkDeleteDialog.loading"
            >
              Delete {{ selectedItems.length }} Item(s)
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Bulk Upload Dialog -->
      <SellerProductVariantBulkUpload 
        v-model="showBulkUploadDialog"
        @upload-completed="handleUploadCompleted"
      />

      <!-- Upload History Dialog -->
      <SellerProductVariantUploadHistory 
        v-model="showUploadHistoryDialog"
        @refresh-needed="fetchData"
      />
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import SellerProductVariantBulkUpload from "@/components/SellerProductVariantBulkUpload.vue";
import SellerProductVariantUploadHistory from "@/components/SellerProductVariantUploadHistory.vue";

export default {
  name: "SellerProductVariantsIndexPage",
  components: {
    SellerProductVariantBulkUpload,
    SellerProductVariantUploadHistory,
  },
  data() {
    return {
      sellerProductVariants: [],
      isLoading: false,
      isExporting: false,
      searchKeyword: "",
      searchTimeout: null,
      totalItems: 0,
      currentPage: 1,
      itemsPerPage: 15,
      showDeleted: false,
      sellerProductId: null, // For filtering by specific seller product
      sellerProductInfo: null, // Store seller product details
      filters: {
        is_active: null,
        brand_name: null,
        category_id: null,
        stock_filter: null,
        status: null,
      },
      brands: [],
      categories: [],
      deleteDialog: {
        show: false,
        item: null,
        loading: false,
      },
      selectedItems: [],
      bulkDeleteDialog: {
        show: false,
        loading: false,
      },
      // Dialog states
      showBulkUploadDialog: false,
      showUploadHistoryDialog: false,
      baseHeaders: [
        { text: "#", value: "row_number", sortable: false, width: "60px", align: "center" },
        { text: "Seller Code", value: "seller_code", sortable: false, width: "120px" },
        { text: "Seller", value: "seller", sortable: false, width: "180px" },
        { text: "SKU", value: "sku", sortable: false, width: "140px" },
        {
          text: "Product Variant",
          value: "product_variant",
          sortable: false,
          width: "180px",
        },
        { text: "Brand", value: "brand", sortable: false, width: "120px" },
        { text: "Category", value: "category", sortable: false, width: "130px" },
        { text: "HSN Code", value: "hsn_code", sortable: false, width: "110px" },
        { text: "MRP", value: "mrp", sortable: false, width: "100px" },
        { text: "Stock", value: "stock", sortable: false, width: "100px" },
      ],
      statusItems: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      stockFilterItems: [
        { text: "In Stock", value: "in_stock" },
        { text: "Out of Stock", value: "out_of_stock" },
        { text: "Low Stock", value: "low_stock" },
      ],
      variantStatusItems: [
        { text: "Selling", value: true },
        { text: "Not Selling", value: false },
      ],
      selectedItemsPerPage: 25, // Default value for pagination toggle
    };
  },
  computed: {
    pageTitle() {
      if (this.sellerProductId && this.sellerProductInfo) {
        return `Product Variants - ${
          this.sellerProductInfo.product?.name || "Unknown Product"
        }`;
      }
      return "Seller Product Variants";
    },
    pageSubtitle() {
      if (this.sellerProductId && this.sellerProductInfo) {
        const seller = this.sellerProductInfo.user;
        const sellerName = seller
          ? `${seller.first_name || ""} ${seller.last_name || ""}`.trim() ||
            seller.email
          : "Unknown Seller";
        return `Managing variants for seller: ${sellerName}`;
      }
      return "Manage seller product variants, pricing, and inventory";
    },

    hasActiveFilters() {
      return (
        this.searchKeyword ||
        this.filters.is_active !== null ||
        this.filters.brand_name !== null ||
        this.filters.category_id !== null ||
        this.filters.stock_filter !== null ||
        this.filters.status !== null ||
        this.showDeleted
      );
    },

    // Dynamically build headers based on user position
    headers() {
      const headers = [...this.baseHeaders];

      // Determine which discount columns to show based on user position
      const position = this.userPosition?.name?.toUpperCase();
      const isAdminUser = this.isAdmin || this.isSuperAdmin;

      // Add discount columns based on position
      if (isAdminUser) {
        // Admin sees all discounts
        headers.push(
          { text: "Seller Discount %", value: "seller_discount", sortable: false, width: "130px" },
          { text: "HT Discount %", value: "ht_discount", sortable: false, width: "120px" },
          { text: "Consumer Discount %", value: "consumer_discount", sortable: false, width: "150px" },
          { text: "Doctor Discount %", value: "doctor_discount", sortable: false, width: "140px" }
        );
      } else if (position === 'SELLER') {
        headers.push(
          { text: "Seller Discount %", value: "seller_discount", sortable: false, width: "130px" }
        );
      } else if (position === 'DOCTOR') {
        headers.push(
          { text: "Doctor Discount %", value: "doctor_discount", sortable: false, width: "140px" }
        );
      } else if (position === 'CONSUMER') {
        headers.push(
          { text: "Consumer Discount %", value: "consumer_discount", sortable: false, width: "150px" }
        );
      }

      // Add remaining columns
      headers.push(
        { text: "Created By", value: "created_by", sortable: false, width: "140px" },
        { text: "Created Date", value: "created_at", sortable: false, width: "150px" },
        { text: "Actions", value: "actions", sortable: false, width: "120px", align: "center" }
      );

      return headers;
    },
  },
  mounted() {
    // Check if we have a seller product ID in the route
    if (this.$route.params.sellerProductId) {
      this.sellerProductId = this.$route.params.sellerProductId;
      this.fetchSellerProductInfo();
    }
    this.fetchData();
    this.loadMasterData();
  },
  methods: {
    async fetchData() {
      try {
        this.isLoading = true;

        const params = {
          page: this.currentPage,
          rowsPerPage: this.itemsPerPage,
          show_deleted: this.showDeleted,
        };

        if (this.searchKeyword) {
          params.search_keyword = this.searchKeyword;
        }

        // Add seller product filtering if we're viewing variants for a specific seller product
        if (this.sellerProductId) {
          params.seller_product_id = this.decryptIt(this.sellerProductId);
        }

        Object.keys(this.filters).forEach((key) => {
          if (this.filters[key] !== null) {
            params[key] = this.filters[key];
          }
        });

        const response = await axios.get("/seller_product_variants", {
          params,
        });

        this.sellerProductVariants = response.data.data || [];
        this.totalItems = response.data.count || 0;
        this.selectedItems = [];
      } catch (error) {
        console.error("Error fetching data:", error);
        this.$toast.error("Failed to load seller product variants");
      } finally {
        this.isLoading = false;
      }
    },

    async fetchSellerProductInfo() {
      if (!this.sellerProductId) return;

      try {
        const response = await axios.get(
          `/seller_products/${this.sellerProductId}`
        );
        this.sellerProductInfo = response.data.data;
      } catch (error) {
        console.error("Error fetching seller product info:", error);
        this.$toast.error("Failed to load seller product information");
      }
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchData();
      }, 500);
    },

    applyFilters() {
      this.currentPage = 1;
      this.fetchData();
    },

    handlePageChange(page) {
      this.currentPage = page;
      this.fetchData();
    },

    handleItemsPerPageChange(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1;
      this.fetchData();
    },

    editItem(item) {
      this.$router.push(`/seller-product-variants/${item.id}`);
    },

    viewItem(item) {
      this.$router.push(`/seller-product-variants/${item.id}`);
    },

    confirmDelete(item) {
      this.deleteDialog.item = item;
      this.deleteDialog.show = true;
    },

    async deleteItem() {
      try {
        this.deleteDialog.loading = true;

        await axios.post(
          `/seller_product_variants/delete/${this.encryptIt(this.deleteDialog.item.id)}`
        );

        this.$toast.success("Seller product variant deleted successfully");
        this.deleteDialog.show = false;
        this.fetchData();
      } catch (error) {
        console.error("Error deleting item:", error);
        this.$toast.error("Failed to delete seller product variant");
      } finally {
        this.deleteDialog.loading = false;
      }
    },

    async restoreItem(item) {
      try {
        await axios.post(`/seller_product_variants/restore/${this.encryptIt(item.id)}`);
        this.$toast.success("Seller product variant restored successfully");
        this.fetchData();
      } catch (error) {
        console.error("Error restoring item:", error);
        this.$toast.error("Failed to restore seller product variant");
      }
    },

    async exportToExcel() {
      try {
        this.isExporting = true;

        const params = {
          show_deleted: this.showDeleted,
        };

        if (this.searchKeyword) {
          params.search_keyword = this.searchKeyword;
        }

        Object.keys(this.filters).forEach((key) => {
          if (this.filters[key] !== null) {
            params[key] = this.filters[key];
          }
        });

        const response = await axios.get("/seller_product_variants/excel", {
          params,
          responseType: "blob",
        });

        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `seller_product_variants_${
          new Date().toISOString().split("T")[0]
        }.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);

        this.$toast.success("Export completed successfully");
      } catch (error) {
        console.error("Error exporting data:", error);
        this.$toast.error("Failed to export data");
      } finally {
        this.isExporting = false;
      }
    },

    getSellerName(seller) {
      if (!seller) return "N/A";
      return (
        `${seller.first_name || ""} ${seller.last_name || ""}`.trim() ||
        seller.email ||
        "N/A"
      );
    },

    getInitials(seller) {
      if (!seller) return "NA";
      const firstName = seller.first_name || "";
      const lastName = seller.last_name || "";
      return (
        `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "NA"
      );
    },

    getStockColor(stock) {
      if (stock <= 0) return "error";
      if (stock <= 10) return "warning";
      if (stock <= 50) return "info";
      return "success";
    },

    getStockTextColor(stock) {
      if (stock <= 0) return "white";
      if (stock <= 10) return "black";
      return "white";
    },

    getStockStatus(stock) {
      if (stock <= 0) return "Out of Stock";
      if (stock <= 10) return "Low Stock";
      if (stock <= 50) return "Medium Stock";
      return "High Stock";
    },

    // Price calculation methods - treating discounts as percentages
    getSellerPrice(item) {
      const mrp = Number(item.maximum_retail_price || 0);
      const discountPercent = Number(item.seller_discount || 0);
      const discountAmount = (mrp * discountPercent) / 100;
      return (mrp - discountAmount).toFixed(2);
    },

    getDoctorPrice(item) {
      const mrp = Number(item.maximum_retail_price || 0);
      const discountPercent = Number(item.doctor_discount || 0);
      const discountAmount = (mrp * discountPercent) / 100;
      return (mrp - discountAmount).toFixed(2);
    },

    getConsumerPrice(item) {
      const mrp = Number(item.maximum_retail_price || 0);
      const discountPercent = Number(item.consumer_discount || 0);
      const discountAmount = (mrp * discountPercent) / 100;
      return (mrp - discountAmount).toFixed(2);
    },

    // Helper methods to get discount amounts in currency
    getSellerDiscountAmount(item) {
      const mrp = Number(item.maximum_retail_price || 0);
      const discountPercent = Number(item.seller_discount || 0);
      return ((mrp * discountPercent) / 100).toFixed(2);
    },

    getDoctorDiscountAmount(item) {
      const mrp = Number(item.maximum_retail_price || 0);
      const discountPercent = Number(item.doctor_discount || 0);
      return ((mrp * discountPercent) / 100).toFixed(2);
    },

    getConsumerDiscountAmount(item) {
      const mrp = Number(item.maximum_retail_price || 0);
      const discountPercent = Number(item.consumer_discount || 0);
      return ((mrp * discountPercent) / 100).toFixed(2);
    },

    // HT (Healthy Tantra) price calculation if needed
    getHTPrice(item) {
      const mrp = Number(item.maximum_retail_price || 0);
      const discountPercent = Number(item.ht_discount || 0);
      const discountAmount = (mrp * discountPercent) / 100;
      return (mrp - discountAmount).toFixed(2);
    },

    getHTDiscountAmount(item) {
      const mrp = Number(item.maximum_retail_price || 0);
      const discountPercent = Number(item.ht_discount || 0);
      return ((mrp * discountPercent) / 100).toFixed(2);
    },

    // New UI methods
    getActiveFiltersCount() {
      let count = 0;
      if (this.searchKeyword) count++;
      if (this.filters.is_active !== null) count++;
      if (this.filters.brand_name !== null) count++;
      if (this.filters.category_id !== null) count++;
      if (this.filters.stock_filter !== null) count++;
      if (this.filters.status !== null) count++;
      if (this.showDeleted) count++;
      return count;
    },

    clearAllFilters() {
      this.searchKeyword = null;
      this.filters.is_active = null;
      this.filters.brand_name = null;
      this.filters.category_id = null;
      this.filters.stock_filter = null;
      this.filters.status = null;
      this.showDeleted = false;
      this.currentPage = 1;
      this.fetchData();
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.currentPage = 1;
      this.fetchData();
    },

    // Handle bulk upload completion
    handleUploadCompleted(result) {
      this.showBulkUploadDialog = false;
      this.fetchData(); // Refresh the data table
      this.$toast.success(`Bulk upload completed: ${result.created} created, ${result.updated} updated`);
    },

    // Format date helper
    formatDate(dateString) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    },

    confirmBulkDelete() {
      this.bulkDeleteDialog.show = true;
    },

    async bulkDeleteItems() {
      try {
        this.bulkDeleteDialog.loading = true;

        const ids = this.selectedItems.map((item) => item.id);
        const response = await axios.post("/seller_product_variants/bulk_delete", { ids });

        this.$toast.success(response.data.message || "Selected variants deleted successfully");
        this.selectedItems = [];
        this.bulkDeleteDialog.show = false;
        this.fetchData();
      } catch (error) {
        console.error("Error bulk deleting items:", error);
        this.$toast.error("Failed to delete selected variants");
      } finally {
        this.bulkDeleteDialog.loading = false;
      }
    },

    // Load master data for filters
    async loadMasterData() {
      try {
        const response = await axios.get("/masters?context=seller_product");
        const masters = response.data;

        // Load brands
        if (masters.brands) {
          this.brands = masters.brands;
        }

        // Load categories
        if (masters.categories) {
          this.categories = masters.categories;
        }
      } catch (error) {
        console.error("Error loading master data:", error);
        this.$toast.error("Failed to load filter options");
      }
    },
  },
};
</script>

<style scoped>
/* Professional Styling */
.page-header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-left: 4px solid var(--v-primary-base);
}

.filter-card {
  background: #fafafa;
  border: 1px solid #e0e0e0;
}

.data-table-card {
  background: #ffffff;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.search-field >>> .v-input__control {
  min-height: 36px;
}

.filter-field >>> .v-input__control {
  min-height: 36px;
}

.show-deleted-checkbox >>> .v-input__control {
  min-height: 36px;
}

.professional-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.professional-table >>> .v-data-table-header {
  background-color: #f5f5f5;
}

.professional-table >>> .v-data-table-header th {
  font-weight: 600;
  color: #424242;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }

  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .page-title {
    font-size: 1rem;
  }

  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-chip {
  font-weight: 500;
}

/* Professional Transitions */
.v-card,
.v-btn {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>